import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, CheckCheck } from "lucide-react";
import api from "@/lib/api";

const ago = (iso) => {
  if (!iso) return "";
  const s = (Date.now() - new Date(iso).getTime()) / 1000;
  if (s < 3600) return `${Math.max(1, Math.floor(s / 60))}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

export default function NotificationBell() {
  const [notes, setNotes] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const navigate = useNavigate();

  const load = () => api.get("/notifications").then((r) => {
    setNotes(r.data.notifications || []);
    setUnread(r.data.unread || 0);
  }).catch(() => {});

  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => { clearInterval(t); document.removeEventListener("mousedown", close); };
  }, []);

  const openItem = (n) => {
    setOpen(false);
    if (!n.read) api.post("/notifications/read-all").then(load).catch(() => {});
    if (n.link) navigate(n.link);
  };

  return (
    <div className="relative" ref={ref}>
      <button onClick={() => { setOpen(!open); if (!open) load(); }} className="relative p-2 text-slate-500 hover:text-slate-800 transition-colors" data-testid="nav-notifications-bell">
        <Bell className="w-5 h-5" />
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-4 h-4 px-1 rounded-full bg-rose-600 text-white text-[9px] font-bold flex items-center justify-center" data-testid="nav-notifications-badge">{unread > 9 ? "9+" : unread}</span>
        )}
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-2 w-80 rounded-2xl border border-slate-200 bg-white shadow-xl overflow-hidden z-50" data-testid="nav-notifications-panel">
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
            <p className="text-sm font-extrabold text-slate-900">Notifications</p>
            {unread > 0 && (
              <button onClick={() => api.post("/notifications/read-all").then(load).catch(() => {})} className="text-[11px] font-bold text-rose-600 hover:text-rose-700 flex items-center gap-1" data-testid="nav-notifications-read-all">
                <CheckCheck className="w-3.5 h-3.5" /> Mark all read
              </button>
            )}
          </div>
          <div className="max-h-80 overflow-y-auto">
            {notes.length === 0 && <p className="text-xs text-slate-400 text-center py-8">No notifications yet.</p>}
            {notes.map((n) => (
              <button key={n.id} onClick={() => openItem(n)} className={`w-full text-left px-4 py-3 border-b border-slate-50 last:border-0 hover:bg-slate-50 ${n.read ? "" : "bg-rose-50/40"}`} data-testid={`nav-notification-${n.id}`}>
                <p className={`text-xs ${n.read ? "font-semibold text-slate-600" : "font-extrabold text-slate-900"}`}>{n.title}</p>
                <p className="text-[11px] text-slate-500 mt-0.5 line-clamp-2">{n.body}</p>
                <p className="text-[10px] text-slate-400 mt-1">{ago(n.created_at)}</p>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
